import { useState } from 'react';
import { Gift } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  SubBar,
  useConfirm,
} from '@/design/components';
import { useImageUrl } from '@/features/products/api';
import { formatDate } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import {
  affordability,
  redemptionStatusLabel,
  useMyPoints,
  useMyRedemptions,
  useRedeem,
  useRewards,
  type Reward,
} from './api';

/** חנות הפרסים (PRD 5.8.4). */
export function RewardsPage() {
  const confirm = useConfirm();
  const rewards = useRewards();
  const points = useMyPoints();
  const redemptions = useMyRedemptions();
  const redeem = useRedeem();
  const [error, setError] = useState<string | null>(null);

  if (rewards.isPending || points.isPending) return <Skeleton className="m-4 h-40" />;
  if (rewards.isError) return <ErrorState onRetry={() => void rewards.refetch()} />;

  const balance = points.data?.balance ?? 0;
  const all = rewards.data ?? [];
  const mine = redemptions.data ?? [];

  const request = (reward: Reward) => {
    setError(null);
    confirm.ask({
      title: 'מימוש פרס',
      tone: 'warn',
      body: (
        <>
          <p className="m-0">"{reward.name}"</p>
          <p className="m-0 text-body-sm text-muted">
            ירדו לך {reward.cost_points} נקודות, ותישאר/י עם {balance - reward.cost_points}.
            הבקשה תעבור לאישור מנהל/ת.
          </p>
        </>
      ),
      confirmLabel: `מימוש תמורת ${reward.cost_points} נקודות`,
      onConfirm: () =>
        void redeem.mutateAsync(reward.id).catch((caught) => setError(errorMessage(caught))),
    });
  };

  return (
    <div className="grid content-start gap-4">
      <SubBar title="חנות הפרסים" subtitle={`יש לך ${balance} נקודות`} />

      <div className="grid gap-6 px-4 pb-8">
        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        {all.length === 0 && (
          <EmptyState
            title="אין פרסים זמינים"
            description="כשההנהלה תוסיף פרסים הם יופיעו כאן."
          />
        )}

        {all.length > 0 && (
          <ul className="m-0 grid list-none gap-2 p-0">
            {all.map((reward) => (
              <li key={reward.id}>
                <RewardRow
                  reward={reward}
                  balance={balance}
                  pending={redeem.isPending}
                  onRedeem={() => request(reward)}
                />
              </li>
            ))}
          </ul>
        )}

        {mine.length > 0 && (
          <section className="grid gap-2">
            <h2 className="m-0 font-display text-display-m font-semibold">הבקשות שלי</h2>
            <ul className="m-0 grid list-none gap-2 p-0">
              {mine.map((item) => (
                <li
                  key={item.id}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-raised p-3"
                >
                  <span className="grow text-body text-ink">{item.rewards?.name}</span>
                  <span className="text-label text-muted">{formatDate(item.created_at)}</span>
                  <StatusBadge
                    tone={
                      item.status === 'rejected'
                        ? 'bad'
                        : item.status === 'requested'
                          ? 'warn'
                          : 'good'
                    }
                  >
                    {redemptionStatusLabel[item.status]}
                  </StatusBadge>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      {confirm.dialog}
    </div>
  );
}

function RewardRow({
  reward,
  balance,
  pending,
  onRedeem,
}: {
  reward: Reward;
  balance: number;
  pending: boolean;
  onRedeem: () => void;
}) {
  const image = useImageUrl(reward.image_path);
  const { canAfford, missing } = affordability(reward, balance);
  const soldOut = reward.stock !== null && reward.stock <= 0;

  return (
    <div className="flex items-center gap-3 rounded-md border border-line bg-raised p-3">
      <span className="grid size-14 shrink-0 place-items-center overflow-hidden rounded-sm bg-olive-soft">
        {image.data ? (
          <img src={image.data} alt="" className="size-full object-cover" />
        ) : (
          <Gift size={24} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        )}
      </span>

      <span className="grid min-w-0 grow gap-0.5">
        <span className="text-title font-medium text-ink">{reward.name}</span>
        {reward.description && (
          <span className="text-body-sm text-muted">{reward.description}</span>
        )}
        <span className="text-label tabular text-muted">
          {reward.cost_points} נקודות
          {reward.stock !== null && ` · נשארו ${Math.max(0, reward.stock)}`}
        </span>
      </span>

      {soldOut ? (
        <StatusBadge tone="bad">אזל</StatusBadge>
      ) : canAfford ? (
        <Button variant="primary" disabled={pending} onClick={onRedeem}>
          לממש
        </Button>
      ) : (
        <span className="shrink-0 text-body-sm text-muted">חסרות {missing}</span>
      )}
    </div>
  );
}
